export interface RecoveryCredentials {
  tokenHash: string | null;
  accessToken: string | null;
  refreshToken: string | null;
  type: string | null;
  error: string | null;
  errorDescription: string | null;
}

const RECOVERY_STORAGE_KEY = 'fe_recovery_credentials';

function parseParamString(paramString: string): URLSearchParams {
  const normalized = paramString.startsWith('#') ? paramString.slice(1) : paramString;
  return new URLSearchParams(normalized);
}

export function parseRecoveryCredentials(): RecoveryCredentials {
  const hashParams = parseParamString(window.location.hash);
  const queryParams = new URLSearchParams(window.location.search);

  const get = (key: string) => hashParams.get(key) || queryParams.get(key);

  return {
    tokenHash: get('token_hash'),
    accessToken: get('access_token'),
    refreshToken: get('refresh_token'),
    type: get('type'),
    error: get('error'),
    errorDescription: get('error_description'),
  };
}

export function hasValidRecoveryCredentials(creds: RecoveryCredentials): boolean {
  if (creds.error) {
    return false;
  }
  if (creds.tokenHash && creds.type === 'recovery') {
    return true;
  }
  if (creds.accessToken && creds.type === 'recovery') {
    return true;
  }
  return false;
}

export function loadRecoveryCredentials(): RecoveryCredentials {
  const fromUrl = parseRecoveryCredentials();
  if (hasValidRecoveryCredentials(fromUrl) || fromUrl.error) {
    sessionStorage.setItem(RECOVERY_STORAGE_KEY, JSON.stringify(fromUrl));
    return fromUrl;
  }

  const stored = sessionStorage.getItem(RECOVERY_STORAGE_KEY);
  if (stored) {
    try {
      return JSON.parse(stored) as RecoveryCredentials;
    } catch {
      sessionStorage.removeItem(RECOVERY_STORAGE_KEY);
    }
  }

  return fromUrl;
}

export function clearRecoveryCredentials(): void {
  sessionStorage.removeItem(RECOVERY_STORAGE_KEY);
}

/** Move recovery tokens onto /reset-password/confirm before React Router can strip the hash. */
export function normalizeRecoveryUrl(): void {
  if (window.location.pathname === '/reset-password/confirm') {
    return;
  }

  const creds = parseRecoveryCredentials();
  const hasRecoveryPayload =
    hasValidRecoveryCredentials(creds) || Boolean(creds.error);

  if (!hasRecoveryPayload) {
    return;
  }

  const target = `/reset-password/confirm${window.location.search}${window.location.hash}`;
  window.history.replaceState(null, '', target);
}

/** Capture tokens before React StrictMode remounts can lose URL params. */
export function bootstrapRecoveryCredentials(): void {
  normalizeRecoveryUrl();
  const creds = loadRecoveryCredentials();
  const onConfirm = window.location.pathname === '/reset-password/confirm';
  const hasUrlParams = Boolean(window.location.search || window.location.hash);

  if (
    onConfirm &&
    hasUrlParams &&
    (hasValidRecoveryCredentials(creds) || creds.error)
  ) {
    window.history.replaceState(null, '', window.location.pathname);
  }
}
